import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { FormTableField, FormTableFieldType } from '@src/components/Form/Table'
import FormModalAutocomplete from '@src/components/FormModal/Autocomplete'
import FormModalBooleanRadioGroup from '@src/components/FormModal/BooleanRadioGroup'
import FormModalCheckboxGroup from '@src/components/FormModal/CheckboxGroup'
import FormModalNumericField from '@src/components/FormModal/Numericfield'
import FormModalSelect from '@src/components/FormModal/Select'
import FormModalTextarea from '@src/components/FormModal/Textarea'
import FormModalTextField from '@src/components/FormModal/Textfield'
import { useModalForm } from '@src/components/FormModal/useModalForm'
import React from 'react'
import styled from 'styled-components'

interface Props<T> {
  open: boolean
  title: string
  data: {
    rowData: T | null
    fields: FormTableField<T>[]
    columns: {
      title: string
      fieldKeys: (keyof T)[]
    }[]
    extra?: any
  }
  onSuccess(form: T): void
  onClose(): void
  onCancel(): void
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  fullWidth?: boolean
}

function MaterialSpraySealModal<T>(props: Props<T>) {
  const {
    open,
    title,
    data: { rowData, fields, columns, extra },
    onSuccess,
    onClose,
    onCancel,
    maxWidth = 'md',
    fullWidth = true,
  } = props

  const [form, setForm, setField] = useModalForm<T>(fields, rowData)
  
  // Dynamic fields that update based on form state
  const getDynamicFields = () => {
    return fields.map(field => {
      if (field.id === 'sprayBinderType') {
        return {
          ...field,
          disabled: !form?.sprayDepot || form.sprayDepot === null
        }
      }
      // Hide legacy depot field since we're not fully backward compatible for editing
      if (field.key === 'depot') {
        return {
          ...field,
          hidden: true
        }
      }
      return field
    })
  }

  const dynamicFields = getDynamicFields()

  const handleSave = () => {
    // Require sprayDepot for all saves (new and edit)
    if (!(form as any)?.sprayDepot) {
      alert('Please select a Spray Depot before saving.')
      return
    }
    
    onSuccess(form)
  }

  const renderField = (field: FormTableField<T>) => {
    const { type } = field
    
    // Don't render hidden fields
    if ((field as any).hidden) {
      return null
    }
    
    switch (type) {
      case FormTableFieldType.Text: {
        return <FormModalTextField
          label={field.label || ''}
          id={field.id || ''}
          defaultValue={form[field.key]}
          disabled={field.disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setField(field.key, e.target.value)
          }}
        />
      }
      case FormTableFieldType.Number: {
        return <FormModalNumericField
          label={field.label || ''}
          value={form[field.key]}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setField(field.key, e.target.value)
          }}
        />
      }
      case FormTableFieldType.Textarea: {
        return <FormModalTextarea
          label={field.label || ''}
          value={form[field.key]}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setField(field.key, e.target.value)
          }}
        />
      }
      case FormTableFieldType.Autocomplete: {
        const isSprayDepot = field.key === 'sprayDepot'
        const isSprayBinderType = field.id === 'sprayBinderType'
        const isDisabled = field.disabled
        
        // If field is disabled, show a read-only text field instead
        if (isDisabled) {
          return <FormModalTextField
            label={field.label || ''}
            defaultValue={form[field.key] ? (field.format ? field.format(form[field.key]) : form[field.key].description || form[field.key].depotName || '') : ''}
            disabled={true}
            onChange={() => {}}
          />
        }
        
        return <FormModalAutocomplete
          extra={{
            depotId: extra && extra.depotId,
            stateId: extra && extra.stateId,
            // Pass the selected depot ID for spray binder type filtering
            ...(isSprayBinderType && (form as any)?.sprayDepot && { 
              locationId: (form as any)?.sprayDepot?.sprayDepotId 
            })
          }}
          type={field.id || (field.key as string)}
          label={field.label || ''}
          value={form[field.key]}
          onChange={async (item: any) => {
            // If spray depot is selected, clear the binder type
            if (isSprayDepot) {
              setForm({
                ...form,
                [field.key]: item,
                binderType: null // Clear binder type when depot changes
              })
            } else {
              setField(field.key, item)
            }
          }}
        />
      }
      case FormTableFieldType.Select: {
        return <FormModalSelect
          label={field.label}
          type={field.id || field.key as string}
          disabled={field.disabled}
          value={form[field.key]}
          onChange={(item: any) => {
            setField(field.key, item)
          }}
        />
      }
      case FormTableFieldType.BooleanRadioGroup: {
        return <FormModalBooleanRadioGroup
          label={field.label || ''}
          value={form[field.key]}
          disabled={field.disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            let booleanValue = false
            if ((e.target as HTMLInputElement).value === 'true') {
              booleanValue = true
            }
            setField(field.key, booleanValue)
          }}
        />
      }
      case FormTableFieldType.CheckboxGroup: {
        return <FormModalCheckboxGroup
          label={field.label || ''}
          value={form[field.key]}
          options={field.options}
          disabled={field.disabled}
          onChange={(item: any) => {
            setField(field.key, item)
          }}
        />
      }
      default:
        return null
    }
  }

  if (form === null) {
    return null
  }

  return (
    <Dialog
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      open={open}
      onClose={onClose}
    >
      <DialogTitle>
        <ModalTitle>{title}</ModalTitle>
      </DialogTitle>
      <DialogContent>
        <SectionWrapper>
          {columns.map((column: { title: string, fieldKeys: (keyof T)[] }, index: number) => {
            return <Section key={column.title || index}>
              <Title>{column.title || ''}</Title>
              {dynamicFields
                .filter((field: FormTableField<T>) => column.fieldKeys.includes(field.key))
                .map((field: FormTableField<T>) => {
                  return <FieldWrapper key={field.key as string}>
                    {renderField(field)}
                  </FieldWrapper>
                })
              }
            </Section>
          })}
        </SectionWrapper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const ModalTitle = styled.h3`
  margin: 0;
`

const SectionWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`

const Section = styled.div`
  flex: 1;
  min-width: 200px;
`

const Title = styled.h4`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
`

const FieldWrapper = styled.div`
  margin-bottom: 16px;
`

export default MaterialSpraySealModal